import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import styles from "./Step2EmailVerification.module.css";
import parentStyles from "../CreatorApplication.module.css";
import PinkEnvelopeIcon from "../icons/PinkEnvelopeIcon";
import { verifyEmailV2API, sendOtpToEmailV2API } from "../../../../api/userApi";
import { showToast } from "../../../../utils/toast";
import { setTokens } from "../../../../utils/tokens";
import { setUser } from "../../../../redux/modules/userSlice";
import { identifyMixpanelUser, setMixpanelPeopleProfile } from "../../../../config/mixpanel";

const schema = yup.object({
  otp: yup
    .string()
    .length(6, "Please enter all 6 digits")
    .required("Verification code is required"),
}).required();

type FormData = yup.InferType<typeof schema>;

interface Step2Props {
  onNext: () => void;
  onBack: () => void;
  email?: string;
  autoSendOtp?: boolean;
  canGoBack?: boolean;
}

export default function Step2EmailVerification({
  onNext,
  onBack,
  email,
  autoSendOtp,
  canGoBack = true,
}: Step2Props) {
  const dispatch = useDispatch();
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const [timeLeft, setTimeLeft] = useState(299); // 4:59 in seconds
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const hasAutoSentRef = useRef(false);

  const sendOtp = async () => {
    if (isResending) return;

    setIsResending(true);
    try {
      const userStr = localStorage.getItem("user");
      const user = userStr ? JSON.parse(userStr) : null;
      const targetEmail = email || user?.email;

      const payload = targetEmail ? { email: targetEmail } : {};
      const res: any = await sendOtpToEmailV2API(payload);
      const resData = res?.data || res;

      if (resData?.success || res?.status === 200 || res?.status === 201) {
        showToast(resData?.message || "Verification code sent to your email!");
        setTimeLeft(299);
      } else {
        showToast(resData?.message || "Failed to send code. Please try again.", "error");
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to send code. Please try again.";
      showToast(errorMessage, "error");
    } finally {
      setIsResending(false);
    }
  };

  const handleResendOtp = async () => {
    if (isResending || timeLeft > 0) return;
    await sendOtp();
  };

  useEffect(() => {
    if (autoSendOtp && !hasAutoSentRef.current) {
      hasAutoSentRef.current = true;
      sendOtp();
    }
  }, [autoSendOtp]);

  useEffect(() => {
    if (timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    register("otp");
  }, [register]);

  const handlePasteValue = (value: string) => {
    const cleanDigits = value.replace(/\D/g, "").slice(0, 6);
    if (!cleanDigits) return;

    const newOtp = [...otp];
    cleanDigits.split("").forEach((digit, i) => {
      newOtp[i] = digit;
    });
    setOtp(newOtp);
    setValue("otp", newOtp.join(""), { shouldValidate: true });

    const nextIndex = Math.min(cleanDigits.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleChange = (index: number, value: string) => {
    if (value.length > 1) {
      handlePasteValue(value);
      return;
    }

    if (value && !/^\d+$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setValue("otp", newOtp.join(""), { shouldValidate: true });

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    handlePasteValue(pastedData);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const onSubmit = async (data: FormData) => {
    setIsVerifying(true);
    try {
      const payload = {
        otp: String(data.otp).trim(),
      };

      const res: any = await verifyEmailV2API(payload);
      const resData = res?.data || res;

      if (resData?.success || res?.status === 200 || res?.status === 201) {
        const tokens = resData?.tokens;
        const user = resData?.user;

        if (tokens) {
          setTokens(tokens);
          if (tokens.access) {
            localStorage.setItem("access_token", tokens.access);
            localStorage.setItem("token", tokens.access);
          }
          if (tokens.refresh) {
            localStorage.setItem("refresh_token", tokens.refresh);
          }
        }
        try {
          const stored = localStorage.getItem("user");
          const currentStoredUser = stored ? JSON.parse(stored) : {};
          const updatedUser = {
            ...currentStoredUser,
            ...(user || {}),
            emailVerified: true,
            validated: true,
          };
          localStorage.setItem("user", JSON.stringify(updatedUser));
          localStorage.setItem("hasRegisteredOrLoggedIn", "true");
          dispatch(setUser(updatedUser));
          identifyMixpanelUser(updatedUser?.id || updatedUser?._id);
          setMixpanelPeopleProfile({
            email: updatedUser?.email || email,
            name: updatedUser?.username || null,
          });
        } catch (e) {
          console.error("Error updating user storage after email verify:", e);
        }

        showToast(resData?.message || "Email verified successfully!");
        onNext();
      } else {
        showToast(resData?.message || "Email verification failed. Please try again.", "error");
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Email verification failed. Please try again.";
      showToast(errorMessage, "error");
    } finally {
      setIsVerifying(false);
    }
  };

  const currentEmailDisplay = email || (() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      return u.email || "you@example.com";
    } catch {
      return "you@example.com";
    }
  })();

  return (
    <div className={parentStyles.formCard}>
      <h1 className={styles.formTitle}>Check your inbox</h1>
      <p className={styles.formSubtitle}>
        We sent a 6-digit code to confirm your email address.
      </p>

      <div className={styles.iconContainer}>
        <div className={styles.iconCircle}>
          <PinkEnvelopeIcon />
        </div>
      </div>

      <div className={styles.emailDisplay}>{currentEmailDisplay}</div>
      <div className={styles.instruction}>
        Enter the code from the verification email.
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className={styles.otpContainer}>
          {[...Array(6)].map((_, i) => (
            <input
              key={i}
              ref={(el) => { inputRefs.current[i] = el; }}
              type="text"
              maxLength={1}
              value={otp[i]}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={handlePaste}
              className={styles.otpInput}
              placeholder="0"
              disabled={isVerifying}
            />
          ))}
        </div>
        
        {errors.otp && (
          <p className={parentStyles.errorText} style={{ textAlign: "center", marginTop: "-16px", marginBottom: "16px" }}>
            {errors.otp.message}
          </p>
        )}

        <div className={styles.resendText}>
          Resend Code :{" "}
          {timeLeft > 0 ? (
            <span className={styles.timer}>After {formatTime(timeLeft)}</span>
          ) : (
            <button
              type="button"
              style={{
                background: "none",
                border: "none",
                color: "#FE5A97",
                cursor: isResending ? "not-allowed" : "pointer",
                fontWeight: 700,
                padding: 0,
                textDecoration: "underline",
                fontSize: "inherit",
                fontFamily: "inherit",
                opacity: isResending ? 0.7 : 1,
              }}
              onClick={handleResendOtp}
              disabled={isResending}
            >
              {isResending ? "Sending..." : "Resend now"}
            </button>
          )}
        </div>

        <div className={styles.actionRow}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => {
              if (!canGoBack) {
                showToast("Back not allowed", "info");
                return;
              }
              onBack();
            }}
            title={!canGoBack ? "Back not allowed" : undefined}
            style={!canGoBack ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
            disabled={isVerifying}
          >
            <ArrowLeft size={18} /> Back
          </button>
          <button type="submit" className={styles.verifyBtn} disabled={isVerifying}>
            {isVerifying ? "Verifying..." : "Verify Email"}
          </button>
        </div>
      </form>

      <div className={styles.footerLink}>
        Code not arriving? <Link to="#" className={styles.blueLink}>Check your Spam Folder</Link>
      </div>
    </div>
  );
}
